import _ from 'lodash';
import { action, observable, observe } from 'mobx';
import {QueryUtility} from 'core/utils';
import {BaseViewStore, TableViewStore} from 'core/stores';

class BaseListViewStore extends BaseViewStore {
    isReady = observable.box(false);
    queryUtility = null;
    tableStore = null;
    name = null;
    routes = null;

    // actions should not be passed in view but only used within view store
    _actions = null;
    get actions() {
        return this._actions ? (typeof this._actions === 'function' ? this._actions() : this._actions) : {};
    }

    constructor(rootStore, { name, actions, routes, queryConfig, tableConfig, autoInit = true, title, hideColon, onCalendar, onPreview, onEdit, openModal, authorization }) {
        super(rootStore);

        this.name = name;
        this.routes = routes;
        //this.authorization = compileAuthorization('portalSection.create' || rootStore.routerStore.getCurrentRoute().authorization);

        this.authorization = {
            create: authorization && authorization.create ? this.hasPermission(authorization.create) : true,
            update: authorization && authorization.update ? this.hasPermission(authorization.update) : true,
            delete: authorization && authorization.delete ? this.hasPermission(authorization.delete) : true,
        };

        const { navigationTitle } = this.rootStore.routerStore.transitionData;
        if(navigationTitle || title){
            this.rootStore.viewStore.setNavigationOptions({
                title: navigationTitle || title,
                hideColon: hideColon,
                onPreview: onPreview,
                onCalendar: onCalendar,
                onEdit: onEdit,
                openModal: openModal,
            });
        }

        this._actions = actions;

        this.queryUtility = new QueryUtility(this.rootStore, async filter => {
            if (!this.actions.find) return;

            this.tableStore.suspend();

            const response = await this.actions.find(filter);
            this.tableStore.setData(response);
            this.tableStore.resume();
        }, queryConfig || {});

        // mobx 6: box.observe was renamed to box.observe_ (private). Use the
        // top-level observe() helper, which is the public API.
        observe(this.isReady, ({ newValue }) => {
            if (autoInit && newValue) {
                this.fetch([this.queryUtility.initialize()]);
            }
        });

        if (tableConfig) {
            const TableStore = tableConfig.TableStore ? tableConfig.TableStore : TableViewStore;
            tableConfig.authorization = this.authorization;
            this.setTableStore(new TableStore(this.queryUtility, tableConfig));
        }
    }

    hasEditPermissions(){
        return this.authorization.edit;
        // let hasPermission = false;
        // if(this.authorization && this.authorization.edit) {
        //     hasPermission = this.hasPermission(this.authorization.edit);
        // } else {
        //     hasPermission = true;
        // }

        // return hasPermission;
    }

    // hasPermission(authorization){
    //     if(this.rootStore.permissionStore.hasPermission(authorization)){
    //         return true;
    //     }

    //     return false;
    // }

    @action.bound
    hasPermission(authorization) {
        return this.rootStore.permissionStore.hasPermission(authorization)
    }

    @action.bound
    setTableStore(tableStore) {
        if(!this.authorization.delete)
            delete tableStore.config.actions.onDelete;
        if(!this.authorization.update)
            delete tableStore.config.actions.onEdit;
            
        const primaryColumn = _.first(tableStore.config.columns);
        if (!primaryColumn.onClick && !primaryColumn.disableClick && this.routes.edit) {
            if(this.hasEditPermissions())
                primaryColumn.onClick = (item) => this.routes.edit(item.id);
        } else if(primaryColumn.onClick && primaryColumn.authorization){
            if(!this.hasPermission(primaryColumn.authorization)) {
                delete primaryColumn.onClick;
            }
        }

        this.tableStore = tableStore;
        this.isReady.set(true);
    }

    @action.bound
    deleteResource(resource) {
        if (!this.actions.find || !this.actions.delete) return;
        
        const { modalStore } = this.rootStore;

        modalStore.showConfirm(
            'LIST_LAYOUT.CONFIRM_DELETE',
            async () => {
                try {
                    await this.actions.delete(resource);
                    this.notifySuccessDelete();
                    await this.queryUtility.fetch();
                } catch (err) {
                    this.notifyErrorDelete(err);
                }
            }
        );
    }

    @action.bound
    notifySuccessDelete() {
        this.rootStore.notificationStore.success('LIST_LAYOUT.SUCCESS_DELETE');
    }

    @action.bound
    notifyErrorDelete(err) {
        this.rootStore.notificationStore.error('LIST_LAYOUT.ERROR_DELETE', err);
    }

    @action.bound
    batchDeleteResources(resources) {
        if (!this.actions.find || !this.actions.batchDelete) return;

        const { modalStore, notificationStore } = this.rootStore;

        modalStore.showConfirm(
            'LIST_LAYOUT.CONFIRM_BATCH_DELETE',
            async () => {
                try {
                    await this.actions.batchDelete(resources);
                    this.notifySuccessBatchDelete();
                    await this.queryUtility.fetch();
                } catch (err) {
                    notificationStore.error('LIST_LAYOUT.ERROR_BATCH_DELETE', err);
                }
            }
        );
    }

    @action.bound
    notifySuccessBatchDelete() {
        this.rootStore.notificationStore.success('LIST_LAYOUT.SUCCESS_BATCH_DELETE');
    }

    @action.bound
    batchUpdateResources(resources) {
        if (!this.actions.find || !this.actions.batchUpdate) return;

        const { modalStore, notificationStore } = this.rootStore;

        modalStore.showConfirm(
            'LIST_LAYOUT.CONFIRM_BATCH_UPDATE',
            async () => {
                try {
                    await this.actions.batchUpdate(resources);
                    this.notifySuccessBatchUpdate();
                    await this.queryUtility.fetch();
                }
                catch (err) {
                    notificationStore.error('LIST_LAYOUT.ERROR_BATCH_UPDATE', err);
                    this.tableStore.resetGridItems();
                }
            }
        )
    }

    @action.bound
    notifySuccessBatchUpdate() {
        this.rootStore.notificationStore.success('LIST_LAYOUT.SUCCESS_BATCH_UPDATE');
    }

    @action.bound
    addResourceToBatchUpdate(event) {
        let { dataItem, field, value } = event;

        dataItem[field] = value;

        var originalDataItem = this.tableStore.originalData.find(obj => {
            return obj.id == dataItem.id
        })
        
        if(originalDataItem && originalDataItem[field] != dataItem[field])
            dataItem['isDirty'] = true;
        else
            dataItem['isDirty'] = false;
        
        this.tableStore.updateDataItems();
    }

    getItem(predicate) {
        return this.tableStore.getItem(predicate);
    }

    setChildNavigationTitle(findPredicate, getProperty, additionalText = null) {
        const item = this.getItem(findPredicate);
        if (item) {
            this.rootStore.routerStore.setTransitionData({
                navigationTitle: getProperty(item) + (additionalText ? ` - ${additionalText}` : '')
            });
        }
    }
}

export default BaseListViewStore;